export enum RwLockState {
	Unlocked = 0,
	Write = 1,
	Read = 2,
}

export class RwLockContainer<T> {
	constructor(
		private _data: T,
		private _lock: RwLock,
	) {}

	state() {
		return this._lock.state();
	}

	async write(fn: (prev: T) => Promise<T> | T): Promise<void> {
		const free = await this._lock.write();
		try {
			this._data = await this._onWrite(await fn(this._data));
		} finally {
			free();
		}
	}

	peek(): T | undefined {
		return this._lock.state() === RwLockState.Write ? undefined : this._data;
	}

	async tryWrite(fn: (prev: T) => Promise<T> | T): Promise<boolean> {
		if (!this._lock.isFree()) return false;
		await this.write(fn);
		return true;
	}

	async read(): Promise<T>;
	async read<R>(fn: (data: T) => Promise<R> | R): Promise<R>;

	async read<R = T>(fn?: (data: T) => Promise<R> | R): Promise<R> {
		const free = await this._lock.read();
		try {
			const data = await this._onRead(this._data);
			return fn ? await fn(data) : (data as unknown as R);
		} finally {
			free();
		}
	}

	async isNull() {
		const free = await this._lock.read();
		const res = !this._data;
		free();
		return res;
	}

	protected _onWrite(prev: T): Promise<T> | T {
		return prev;
	}

	protected _onRead(data: T): Promise<T> | T {
		return data;
	}
}

export class RwLock {
	private _readers = 0;
	private _writer = false;
	private _readQueue: (() => void)[] = [];
	private _writeQueue: (() => void)[] = [];

	static store<T>(data: T) {
		return new RwLockContainer(data, new RwLock());
	}

	isFree(): boolean {
		return !this._writer && this._readers === 0 && this._writeQueue.length === 0;
	}

	state(): RwLockState {
		if (this._writer) return RwLockState.Write;
		if (this._readers > 0) return RwLockState.Read;
		return RwLockState.Unlocked;
	}

	async read(): Promise<() => void> {
		const canReadImmediately = !this._writer && this._writeQueue.length === 0;
		if (canReadImmediately) this._readers++;
		else await new Promise<void>((resolve) => this._readQueue.push(resolve));

		return () => {
			this._readers--;
			this._dispatch();
		};
	}

	async write(): Promise<() => void> {
		const canWriteImmediately = !this._writer && this._readers === 0;
		if (canWriteImmediately) this._writer = true;
		else await new Promise<void>((resolve) => this._writeQueue.push(resolve));

		return () => {
			this._writer = false;
			this._dispatch();
		};
	}

	// A woken waiter resumes only in a later microtask, so the lock is handed over here, before it runs:
	// anything that looks at the lock in between must see it taken.
	private _dispatch() {
		if (!this._writer && this._readers === 0 && this._writeQueue.length > 0) {
			this._writer = true;
			this._writeQueue.shift()();
		} else if (!this._writer && this._writeQueue.length === 0 && this._readQueue.length > 0) {
			while (this._readQueue.length > 0) {
				this._readers++;
				this._readQueue.shift()();
			}
		}
	}
}
